import { useState, useEffect, useRef } from "react";
import { Store, Copy, Check, Search, Loader2, ExternalLink, ImagePlus, Percent, BadgeDollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MobileCards, DataCard, EmptyCard } from "@/components/DataCards";
import { api, apiError, API } from "@/lib/api";
import { toast } from "sonner";

const priceStr = (p) => `R$ ${Number(p || 0).toFixed(2)}`;
const computePrice = (base, type, value) => {
  base = Number(base) || 0;
  const v = Number(value) || 0;
  return type === "percent" ? base * (1 + v / 100) : base + v;
};

export default function ResellerStore() {
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [active, setActive] = useState(false);
  const [color, setColor] = useState("#e11d2a");
  const [logo, setLogo] = useState(null);
  const [marginType, setMarginType] = useState("percent");
  const [marginValue, setMarginValue] = useState(0);
  const [appMargins, setAppMargins] = useState({}); // { [id]: { type, value } } overrides only
  const [q, setQ] = useState("");
  const [copied, setCopied] = useState(false);
  const [savingS, setSavingS] = useState(false);
  const [savingM, setSavingM] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoRef = useRef(null);

  const load = () => api.get("/reseller/store").then(({ data }) => {
    setData(data); setName(data.store_name); setSlug(data.store_slug); setActive(data.store_active);
    setColor(data.store_color || "#e11d2a"); setLogo(data.store_logo ? `${API}${data.store_logo}` : null);
    setMarginType(data.margin_type || "percent");
    setMarginValue(data.margin_value ?? 0);
    const m = {};
    (data.apps || []).forEach((a) => { if (a.margin_type && a.margin_value != null) m[a.id] = { type: a.margin_type, value: a.margin_value }; });
    setAppMargins(m);
  }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    setSavingS(true);
    try {
      const { data: r } = await api.put("/reseller/store", {
        store_name: name, store_slug: slug, store_active: active, store_color: color,
        margin_type: marginType, margin_value: Number(marginValue) || 0,
      });
      setSlug(r.store_slug); toast.success("Loja e margem padrão salvas");
    } catch (e) { toast.error(apiError(e)); } finally { setSavingS(false); }
  };
  const onLogo = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Envie uma imagem");
    const fd = new FormData(); fd.append("file", file);
    setUploadingLogo(true);
    try { const { data: r } = await api.post("/reseller/store/logo", fd);
      setLogo(`${API}${r.store_logo}?t=${Date.now()}`); toast.success("Logo atualizado"); }
    catch (err) { toast.error(apiError(err)); } finally { setUploadingLogo(false); }
  };
  const saveMargins = async () => {
    setSavingM(true);
    const payload = {};
    Object.entries(appMargins).forEach(([id, m]) => {
      if (m && m.value !== "" && m.value != null) payload[id] = { type: m.type || "percent", value: Number(m.value) };
    });
    try { const { data: r } = await api.put("/reseller/store/margins", { margins: payload });
      toast.success(`${r.count} margens personalizadas salvas`); }
    catch (e) { toast.error(apiError(e)); } finally { setSavingM(false); }
  };

  // per-row margin helpers
  const rowType = (id) => appMargins[id]?.type || "default";
  const setRowType = (id, type) => setAppMargins((p) => {
    if (type === "default") { const n = { ...p }; delete n[id]; return n; }
    return { ...p, [id]: { type, value: p[id]?.value ?? "" } };
  });
  const setRowValue = (id, value) => setAppMargins((p) => ({ ...p, [id]: { type: p[id]?.type || "percent", value } }));
  const finalFor = (a) => {
    const ov = appMargins[a.id];
    if (ov && ov.value !== "" && ov.value != null) return computePrice(a.price, ov.type, ov.value);
    return computePrice(a.price, marginType, Number(marginValue) || 0);
  };

  const url = slug ? `${window.location.origin}/loja/${slug}` : "";
  const copy = async () => { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  if (!data) return <div className="text-muted-foreground">Carregando...</div>;
  const apps = data.apps.filter((a) => a.name?.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="reseller-store">
      <div className="flex items-center gap-2"><Store className="h-6 w-6 text-primary" /><h2 className="font-heading text-xl font-bold tracking-tight sm:text-2xl">Minha loja</h2></div>

      <div className="grid gap-4 rounded-lg border border-border bg-card p-4 sm:grid-cols-2">
        <div className="space-y-2"><Label>Nome da loja</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Loja do Fulano" data-testid="store-name-input" /></div>
        <div className="space-y-2"><Label>Link (slug)</Label><Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="lojafulano" data-testid="store-slug-input" /></div>

        <div className="space-y-2">
          <Label>Cor da marca</Label>
          <div className="flex items-center gap-2">
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-9 w-12 cursor-pointer rounded border border-border bg-transparent" data-testid="store-color-input" />
            <Input value={color} onChange={(e) => setColor(e.target.value)} className="w-32 font-mono" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Logo da loja</Label>
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-md border border-border bg-muted">
              {logo ? <img src={logo} alt="logo" className="h-full w-full object-contain" /> : <Store className="h-5 w-5 text-muted-foreground" />}
            </span>
            <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="store-logo-input" />
            <Button variant="outline" size="sm" className="gap-2" disabled={uploadingLogo} onClick={() => logoRef.current?.click()} data-testid="store-logo-btn">
              {uploadingLogo ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} Enviar logo
            </Button>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm"><Switch checked={active} onCheckedChange={setActive} data-testid="store-active-switch" /> Loja ativa (visível ao público)</label>
        <div className="flex items-end justify-end"><Button onClick={saveSettings} disabled={savingS} className="gap-2" data-testid="store-save-btn">{savingS && <Loader2 className="h-4 w-4 animate-spin" />} Salvar loja</Button></div>

        {slug && (
          <div className="sm:col-span-2 flex items-center gap-2 rounded-md border border-border bg-muted/40 p-2 text-sm">
            <span className="min-w-0 flex-1 truncate font-mono">{url}</span>
            <Button variant="outline" size="icon" onClick={copy} data-testid="store-copy-btn">{copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}</Button>
            <a href={url} target="_blank" rel="noreferrer"><Button variant="ghost" size="icon"><ExternalLink className="h-4 w-4" /></Button></a>
          </div>
        )}
        <p className="sm:col-span-2 text-xs text-muted-foreground">Personalize logo e cor para dar cara de marca própria. A loja sempre exibe "Powered by VULPIX".</p>
      </div>

      {/* Default margin */}
      <div className="rounded-lg border border-border bg-card p-4" data-testid="default-margin-card">
        <div className="mb-1 font-heading text-base font-semibold">Margem padrão</div>
        <p className="mb-3 text-xs text-muted-foreground">Aplicada a <b>todos</b> os apps sobre o preço base. Você pode sobrescrever por app na tabela abaixo. Preço final = Base + Margem.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="inline-flex overflow-hidden rounded-md border border-border">
            <button onClick={() => setMarginType("percent")} data-testid="margin-type-percent"
              className={`flex items-center gap-1 px-3 py-2 text-sm transition-colors ${marginType === "percent" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
              <Percent className="h-4 w-4" /> Porcentagem
            </button>
            <button onClick={() => setMarginType("reais")} data-testid="margin-type-reais"
              className={`flex items-center gap-1 px-3 py-2 text-sm transition-colors ${marginType === "reais" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
              <BadgeDollarSign className="h-4 w-4" /> Valor (R$)
            </button>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{marginType === "percent" ? "Margem (%)" : "Margem (R$)"}</Label>
            <div className="relative">
              <Input type="number" step="0.01" min="0" value={marginValue} onChange={(e) => setMarginValue(e.target.value)} className="w-40 font-num pr-8" data-testid="default-margin-value-input" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{marginType === "percent" ? "%" : "R$"}</span>
            </div>
          </div>
          <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            Ex.: base R$ 20,00 → final <b className="text-foreground">{priceStr(computePrice(20, marginType, marginValue))}</b>
          </div>
          <Button onClick={saveSettings} disabled={savingS} className="gap-2 ml-auto" data-testid="save-default-margin-btn">{savingS && <Loader2 className="h-4 w-4 animate-spin" />} Salvar margem padrão</Button>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar aplicativo..." className="pl-9" data-testid="store-search" />
        </div>
        <Button onClick={saveMargins} disabled={savingM} className="w-full gap-2 sm:w-auto" data-testid="store-save-margins-btn">{savingM && <Loader2 className="h-4 w-4 animate-spin" />} Salvar margens por app</Button>
      </div>

      <MobileCards testid="store-price-cards">
        {apps.length === 0 && <EmptyCard>Nenhum aplicativo.</EmptyCard>}
        {apps.map((a) => {
          const t = rowType(a.id);
          return (
            <DataCard
              key={a.id}
              testid={`store-card-${a.id}`}
              title={a.name}
              badge={<span className="font-num text-sm font-bold text-primary" data-testid={`final-price-m-${a.id}`}>{priceStr(finalFor(a))}</span>}
              rows={[
                { label: "Preço base", value: <span className="font-num">{a.price != null ? priceStr(a.price) : "—"}</span> },
                { label: "Créditos (fixo)", value: <span className="font-num">{a.credit_cost}</span> },
                {
                  label: "Margem do app", full: true,
                  value: (
                    <div className="mt-1 flex items-center gap-1.5">
                      <select value={t} onChange={(e) => setRowType(a.id, e.target.value)} data-testid={`margin-mode-m-${a.id}`}
                        className="h-9 flex-1 rounded-md border border-border bg-background px-2 text-sm">
                        <option value="default">Padrão</option>
                        <option value="percent">%</option>
                        <option value="reais">R$</option>
                      </select>
                      {t !== "default" && (
                        <Input type="number" step="0.01" min="0" value={appMargins[a.id]?.value ?? ""} placeholder="0"
                          onChange={(e) => setRowValue(a.id, e.target.value)} className="h-9 w-24 font-num" data-testid={`margin-value-m-${a.id}`} />
                      )}
                    </div>
                  ),
                },
              ]}
            />
          );
        })}
      </MobileCards>

      <div className="hidden rounded-lg border border-border bg-card md:block">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Aplicativo</TableHead><TableHead>Preço base</TableHead>
            <TableHead>Créditos (fixo)</TableHead><TableHead>Margem do app</TableHead>
            <TableHead>Preço final (R$)</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {apps.map((a) => {
              const t = rowType(a.id);
              return (
                <TableRow key={a.id} data-testid={`store-row-${a.id}`}>
                  <TableCell className="font-medium">{a.name}</TableCell>
                  <TableCell className="font-num text-muted-foreground">{a.price != null ? priceStr(a.price) : "—"}</TableCell>
                  <TableCell className="font-num text-muted-foreground">{a.credit_cost}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <select value={t} onChange={(e) => setRowType(a.id, e.target.value)} data-testid={`margin-mode-${a.id}`}
                        className="h-9 rounded-md border border-border bg-background px-2 text-sm">
                        <option value="default">Padrão</option>
                        <option value="percent">%</option>
                        <option value="reais">R$</option>
                      </select>
                      {t !== "default" && (
                        <Input type="number" step="0.01" min="0" value={appMargins[a.id]?.value ?? ""} placeholder="0"
                          onChange={(e) => setRowValue(a.id, e.target.value)} className="h-9 w-24 font-num" data-testid={`margin-value-${a.id}`} />
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-num font-semibold" data-testid={`final-price-${a.id}`}>{priceStr(finalFor(a))}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <p className="text-xs text-muted-foreground">O crédito de ativação é fixo (definido pela plataforma) e debitado da SUA carteira de créditos em cada venda. O valor recebido em R$ vai direto ao seu gateway configurado.</p>
    </div>
  );
}
